import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Progress } from "@/components/ui/progress";
import {
  UploadCloud,
  FileText,
  Image as ImageIcon,
  Eye,
  Download,
  Trash2,
  Plane,
  Hotel,
  Mail,
  Phone,
  CalendarClock,
  CalendarRange,
  Users,
  Pencil,
  History,
  Send,
  UserCheck,
  CreditCard,
} from "lucide-react";
import { Semaphore } from "@/components/Semaphore";
import { PaymentBadges } from "@/components/PaymentBadge";
import { DocumentViewer } from "@/components/DocumentViewer";
import { PaymentsSection } from "@/components/PaymentsSection";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { EmailModal } from "@/components/EmailModal";
import { uploadDocument, deleteDocument, documentUrl, updateStatus, getClientAudit } from "@/lib/api";
import { formatBytes, formatDate, isImage } from "@/lib/constants";

const Row = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-2.5">
    <Icon className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
    <div className="min-w-0">
      <p className="text-xs uppercase tracking-wide text-slate-400 font-medium">{label}</p>
      <p className="text-sm text-slate-700 break-words">{value || "—"}</p>
    </div>
  </div>
);

export const ClientDetailSheet = ({
  open,
  onOpenChange,
  client,
  methods,
  labels,
  currency = "USD",
  terminals = [],
  onUpdate,
  onOpenReservation,
  onEdit,
}) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const [viewerDoc, setViewerDoc] = useState(null);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const fileRef = useRef(null);

  const loadAudit = async () => {
    if (!client?.id) return;
    try {
      const data = await getClientAudit(client.id);
      setAuditLogs(data);
    } catch {
      /* noop */
    }
  };

  useEffect(() => {
    if (open && client?.id) {
      loadAudit();
    }
  }, [open, client?.id]);

  if (!client) return null;

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      let updated = client;
      for (const file of Array.from(files)) {
        setProgress(0);
        updated = await uploadDocument(client.id, file, setProgress);
      }
      onUpdate?.(updated);
      toast.success("Documento(s) subido(s)");
    } catch {
      toast.error("Error al subir el documento");
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const removeDoc = async (docId) => {
    try {
      const updated = await deleteDocument(client.id, docId);
      onUpdate?.(updated);
      toast.success("Documento eliminado");
    } catch {
      toast.error("No se pudo eliminar");
    }
  };

  const changeStatus = async (status) => {
    try {
      const updated = await updateStatus(client.id, status);
      onUpdate?.(updated);
    } catch {
      toast.error("No se pudo cambiar el estatus");
    }
  };

  const res = client.reservation || {};
  const totalGuests = (res.adults || 0) + (res.children?.length || 0) + (res.guests?.length || 0);
  const travelText = res.travel_date_open
    ? "Fecha abierta"
    : res.travel_date
    ? formatDate(res.travel_date)
    : "Sin definir";
  const payList = client.payment_methods?.length
    ? client.payment_methods
    : client.payment_method
    ? [client.payment_method]
    : [];

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-full sm:max-w-xl p-0 flex flex-col" data-testid="client-detail-sheet">
          <SheetHeader className="px-6 pt-6 pb-4 border-b border-slate-100 space-y-3">
            <div className="flex items-start justify-between gap-4">
              <div>
                <span className="font-mono text-xs text-slate-400">{client.client_code}</span>
                <SheetTitle className="font-heading text-2xl leading-tight">
                  {client.full_name}
                </SheetTitle>
                <SheetDescription className="sr-only">Ficha del cliente</SheetDescription>
              </div>
              <Semaphore status={client.status} labels={labels} onChange={changeStatus} />
            </div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" data-testid="open-reservation-button" onClick={() => onOpenReservation?.(client)}>
                <Plane className="h-4 w-4 mr-1.5" /> Datos de Reserva
              </Button>
              <Button
                size="sm"
                variant="outline"
                data-testid="send-email-button"
                onClick={() => setEmailModalOpen(true)}
              >
                <Mail className="h-4 w-4 mr-1.5" /> Enviar Correo
              </Button>
              <Button
                size="sm"
                variant="outline"
                data-testid="edit-client-button"
                onClick={() => onEdit?.(client)}
              >
                <Pencil className="h-4 w-4 mr-1.5" /> Editar Cliente
              </Button>
            </div>
            <div className="pt-1 flex flex-wrap items-center gap-2">
              <PaymentBadges list={payList} methods={methods} />
              {client.terminal && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-blue-50 text-blue-700 text-xs font-semibold border border-blue-200/80 shadow-2xs">
                  <CreditCard className="w-3.5 h-3.5 text-blue-500" />
                  Terminal: {client.terminal}
                </span>
              )}
            </div>
          </SheetHeader>

          <ScrollArea className="flex-1">
            <div className="px-6 py-5 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <Row icon={Mail} label="Correo" value={client.email} />
                <Row icon={Phone} label="Teléfono" value={client.phone} />
                <Row icon={Hotel} label="Hotel" value={client.hotel} />
                <Row icon={CreditCard} label="Terminal de Cobro" value={client.terminal || "No asignada"} />
                <Row icon={UserCheck} label="Asesor Asignado" value={client.assigned_to || "General (Sin asignar)"} />
                <Row icon={Users} label="Huéspedes" value={`${totalGuests} en total`} />
                <Row icon={CalendarRange} label="Fecha de viaje" value={travelText} />
              </div>

              {/* Reservation summary */}
              <div className="rounded-xl border border-slate-200 p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="font-heading font-semibold text-slate-800">Resumen de Reserva</h3>
                  <span className="text-xs text-slate-400">
                    {res.adults || 0} adultos · {res.children?.length || 0} niños
                  </span>
                </div>
                <div
                  className="flex items-center gap-2 text-sm text-slate-600"
                  data-testid="travel-date-summary"
                >
                  <CalendarRange className="h-4 w-4 text-slate-400" />
                  <span>Fecha de viaje:</span>
                  <span className="font-medium text-slate-700">{travelText}</span>
                </div>
                {res.children?.length > 0 && (
                  <p className="text-sm text-slate-600">
                    Edades niños: {res.children.map((c) => `${c.age}`).join(", ")} años
                  </p>
                )}
                <div className="flex flex-wrap gap-2 text-xs">
                  <span
                    className={`rounded-full px-2.5 py-1 font-medium ${
                      res.has_flights ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"
                    }`}
                  >
                    {res.has_flights ? "✓ Ya tiene vuelos" : "Sin vuelos"}
                  </span>
                  <span
                    className={`rounded-full px-2.5 py-1 font-medium ${
                      res.we_sold_flights ? "bg-blue-50 text-blue-700" : "bg-slate-100 text-slate-500"
                    }`}
                  >
                    {res.we_sold_flights ? "✓ Vuelos vendidos por nosotros" : "No vendidos por nosotros"}
                  </span>
                </div>
                {res.flights?.length > 0 && (
                  <div className="space-y-2 pt-1">
                    {res.flights.map((f) => (
                      <div
                        key={f.id}
                        className="flex items-center gap-2 rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-600"
                      >
                        <CalendarClock className="h-4 w-4 text-slate-400" />
                        <span className="font-medium text-slate-700">{f.airline || "Aerolínea"}</span>
                        {f.flight_number && <span>· {f.flight_number}</span>}
                        {f.date && <span>· {formatDate(f.date)}</span>}
                        {f.departure_time && <span>· {f.departure_time}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Payments */}
              <PaymentsSection
                client={client}
                methods={methods}
                currency={currency}
                terminals={terminals}
                onUpdate={onUpdate}
              />

              {/* Documents */}
              <div className="space-y-3">
                <h3 className="font-heading font-semibold text-slate-800">
                  Documentos ({client.documents?.length || 0})
                </h3>                <div
                  data-testid="document-upload-zone"
                  onClick={() => fileRef.current?.click()}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragOver(true);
                  }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDragOver(false);
                    handleFiles(e.dataTransfer.files);
                  }}
                  className={`cursor-pointer rounded-xl border-2 border-dashed p-6 text-center transition-colors ${
                    dragOver ? "border-blue-400 bg-blue-50" : "border-slate-200 hover:border-slate-300 bg-slate-50/50"
                  }`}
                >
                  <UploadCloud className="h-8 w-8 text-slate-400 mx-auto mb-2" />
                  <p className="text-sm text-slate-600 font-medium">
                    Arrastra archivos aquí o haz clic para examinar
                  </p>
                  <p className="text-xs text-slate-400 mt-0.5">Sin límite de peso ni formato</p>
                  <input
                    ref={fileRef}
                    type="file"
                    multiple
                    className="hidden"
                    data-testid="document-file-input"
                    onChange={(e) => handleFiles(e.target.files)}
                  />
                </div>

                {uploading && (
                  <div className="space-y-1">
                    <Progress value={progress} className="h-2" />
                    <p className="text-xs text-slate-500">Subiendo... {progress}%</p>
                  </div>
                )}

                <div className="space-y-2">
                  {(client.documents || []).map((d) => (
                    <div
                      key={d.id}
                      data-testid="document-item"
                      className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white p-3 hover:border-slate-300 transition-colors"
                    >
                      <div className="h-9 w-9 rounded-md bg-slate-100 flex items-center justify-center shrink-0">
                        {isImage(d.content_type) ? (
                          <ImageIcon className="h-4 w-4 text-slate-500" />
                        ) : (
                          <FileText className="h-4 w-4 text-slate-500" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-slate-700 truncate">{d.filename}</p>
                        <p className="text-xs text-slate-400">
                          {formatBytes(d.size)} · {formatDate(d.uploaded_at)}
                        </p>
                      </div>
                      <div className="flex items-center gap-0.5 shrink-0">
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 text-slate-400 hover:text-slate-700"
                          data-testid="view-document-button"
                          onClick={() => setViewerDoc(d)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          asChild
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 text-slate-400 hover:text-slate-700"
                        >
                          <a href={documentUrl(client.id, d.id, true)}>
                            <Download className="h-4 w-4" />
                          </a>
                        </Button>
                        <ConfirmDialog
                          title="¿Eliminar documento?"
                          description={`Se eliminará "${d.filename}" de forma permanente.`}
                          testId="confirm-delete-document"
                          onConfirm={() => removeDoc(d.id)}
                          trigger={
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8 text-slate-400 hover:text-red-500"
                              data-testid="delete-document-button"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          }
                        />
                      </div>
                    </div>
                  ))}
                  {(!client.documents || client.documents.length === 0) && (
                    <p className="text-sm text-slate-400 text-center py-2">
                      Aún no hay documentos para este cliente.
                    </p>
                  )}
                </div>
              </div>

              {/* Activity History Timeline */}
              <div className="rounded-xl border border-slate-200 p-4 space-y-3 bg-slate-50/50">
                <div className="flex items-center justify-between">
                  <h3 className="font-heading font-semibold text-slate-800 flex items-center gap-2">
                    <History className="h-4 w-4 text-slate-500" /> Historial de Actividad
                  </h3>
                  <span className="text-[11px] text-slate-400">
                    {auditLogs.length} eventos
                  </span>
                </div>

                {auditLogs.length === 0 ? (
                  <p className="text-xs text-slate-400 py-2">Sin actividad registrada aún.</p>
                ) : (
                  <div className="space-y-2.5 max-h-48 overflow-y-auto pr-1">
                    {auditLogs.map((log) => (
                      <div key={log.id} className="text-xs bg-white p-2.5 rounded-lg border border-slate-200/80 space-y-1">
                        <div className="flex items-center justify-between text-[11px] text-slate-500">
                          <span className="font-semibold text-slate-700">@{log.username}</span>
                          <span className="font-mono text-slate-400">{formatDate(log.created_at)}</span>
                        </div>
                        <p className="text-slate-600 leading-snug">{log.details}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </ScrollArea>
        </SheetContent>
      </Sheet>

      <DocumentViewer
        open={!!viewerDoc}
        onOpenChange={(v) => !v && setViewerDoc(null)}
        clientId={client.id}
        doc={viewerDoc}
      />

      <EmailModal
        open={emailModalOpen}
        onOpenChange={setEmailModalOpen}
        client={client}
        onSent={loadAudit}
      />
    </>
  );
};
